import {
  Message,
  PresenceMember,
  useChatClient,
  useMessages,
  useOccupancy,
  usePresence,
  usePresenceListener,
} from "@ably/chat";
import { useUserData } from "../../../hooks/useUserData";
import { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";
import { ReactionTypeEntity } from "../../../services/gc/types/reactionTypeEntity";
import { MessageReaction, ModerationError } from "../types";
import { useToast } from "@chakra-ui/react";
import { useChannel } from "ably/react";
import { useHandleLogout } from "../../../hooks/useHandleLogout";
import { apiClient } from "../../../services/axiosInstance";
import { ServerResponse } from "../../../types";
import { publishChatJoinedEvent } from "../../../screens/root/api/eventsApi";

interface ModerateResponse {
  flagged: boolean;
  categories?: string[];
}

export function useChat(
  roomId: string,
  guests: string[] = [],
  guestIcon?: string,
) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [messagesBySerial, setMessagesBySerial] = useState<
    Record<string, Message>
  >({});
  const [members, setMembers] = useState<Record<string, PresenceMember>>({});
  const { userData } = useUserData();
  const chat = useChatClient();

  // The total count of present members anon and signed in
  const { presenceMembers } = useOccupancy();

  // Listen for users joining and leaving the room
  const { presence } = usePresenceListener({
    listener: (member) => {
      setMembers((prevMembers) => ({
        ...prevMembers,
        [member.clientId]: {
          ...member,
          extras: {},
          updatedAt: Date.now(),
        } as PresenceMember,
      }));
    },
  });

  usePresence({
    enterWithData: {
      username: userData?.name,
      isGold: userData?.gold,
      avatar: userData?.avatar_tiny_url,
      isAdmin: userData?.is_admin,
      isGuest: guests.includes(userData?.id + ""),
      guestIcon,
    },
    leaveWithData: {
      username: userData?.name,
      isGold: userData?.gold,
      avatar: userData?.avatar_tiny_url,
      isAdmin: userData?.is_admin,
      isGuest: guests.includes(userData?.id + ""),
      guestIcon,
    },
  });

  // Track chat join event
  useEffect(() => {
    publishChatJoinedEvent(roomId);
  }, [roomId]);

  // Global "broadcast" channel that allows the api server
  // to communicate with all chat clients accross channels
  const toast = useToast();
  const handleLogout = useHandleLogout();
  useChannel("gc:broadcast", (message) => {
    if (message.name === "message.banned") {
      if (message.data.clientId === chat.clientId) {
        presence?.leave();
        handleLogout();
        toast({
          title: "You have been banned.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        setMembers((prevMembers) => {
          const newMembers = { ...prevMembers };
          delete newMembers[message.data.clientId];
          return newMembers;
        });
      }
    }
  });

  // Listen for message events in the room and updates
  // the messages state and deletes messages when needed
  const {
    roomStatus,
    send: ablySendMessage,
    update: ablyUpdateMessage,
    deleteMessage: ablyDeleteMessage,
    messages: ablyMessages,
  } = useMessages({
    listener: (message) => {
      saveMessageBySerial(message.message);
    },
  });

  // Get the current members of the room
  useLayoutEffect(() => {
    if (presence) {
      presence.get().then((data) => {
        setMembers(
          data.reduce((acc: Record<string, PresenceMember>, curr) => {
            acc[curr.clientId] = curr;
            return acc;
          }, {}),
        );
      });
    }
  }, [presence]);

  // Get the historical messges in the room
  useLayoutEffect(() => {
    if (!ablyMessages) return;

    let cancelled = false;

    ablyMessages.get({ limit: 50 }).then((messages) => {
      // Don't update state if this effect was cancelled (room changed)
      if (cancelled) return;

      setMessagesBySerial(
        messages.items.reduce(
          (acc, curr) => {
            if (!curr.isDeleted) acc[curr.serial] = curr;
            return acc;
          },
          {} as Record<string, Message>,
        ),
      );
      setIsLoaded(true);
    });

    // Cleanup function to cancel if room changes
    return () => {
      cancelled = true;
    };
  }, [ablyMessages]);

  useLayoutEffect(() => {
    if (roomStatus !== "attached") {
      setIsLoaded(false);
    }
  }, [roomStatus]);

  const saveMessageBySerial = (message: Message) => {
    setMessagesBySerial((prevMessages) => {
      return {
        ...prevMessages,
        [message.serial]: message,
      };
    });
  };

  const handleDeleteMessage = async (message: Message) => {
    const messageToDelete = {
      ...message,
      metadata: {
        ...message.metadata,
        serial: message.serial,
        action: "message.delete",
      },
    };

    saveMessageBySerial({ ...messageToDelete, isDeleted: true });

    await ablyUpdateMessage(message, messageToDelete);
    try {
      await ablyDeleteMessage(messageToDelete);
    } catch (error) {
      console.error("Failed to delete message", error);
      saveMessageBySerial(message);
    }
  };

  // Populates the reaction object by adding or removing
  // the currents user's reactions
  const handleToggleReaction = async (
    message: Message,
    reaction: ReactionTypeEntity,
  ) => {
    const reactions = (message.metadata.reactions ?? {}) as Record<
      string,
      MessageReaction[]
    >;

    if (!reactions[reaction.id]) reactions[reaction.id] = [];

    const reactionArray = reactions[reaction.id];
    const index = reactionArray.findIndex((r) => r.clientId === chat.clientId);
    if (index > -1) {
      reactionArray.splice(index, 1);
    } else {
      reactionArray.push({ clientId: chat.clientId, reaction });
    }

    const updatedMessage = {
      ...message,
      metadata: {
        ...message.metadata,
        serial: message.serial,
        reactions,
        action: "message.update",
      },
    };
    saveMessageBySerial(updatedMessage);
    try {
      await ablyUpdateMessage(message, updatedMessage);
    } catch (error) {
      console.error("Failed to add reaction", error);
      saveMessageBySerial(message);
    }
  };

  const handleSendMessage = async (message: string) => {
    // Send message via Ably Chat SDK first (instant UX)
    const sentMessage = await ablySendMessage({
      text: message,
      metadata: {
        username: userData?.name,
        isGold: userData?.gold,
        avatar: userData?.avatar_tiny_url,
        isAdmin: userData?.is_admin,
        isGuest: guests.includes(userData?.id + ""),
        guestIcon,
      },
    });

    // Then check moderation - backend will delete if flagged
    try {
      const response = await apiClient.post<ServerResponse<ModerateResponse>>(
        "/v1/chat/moderate",
        {
          meta: {},
          payload: {
            message: message,
            room_id: roomId,
            serial: sentMessage.serial,
          },
        },
      );

      const result = response.data.payload.results;
      if (result?.flagged && result?.categories) {
        throw new ModerationError(result.categories);
      }
    } catch (error) {
      if (error instanceof ModerationError) {
        throw error;
      }
      // Don't throw on moderation API errors - message was already sent
      console.error("Failed to moderate message", error);
    }
  };

  return {
    isLoaded,
    messageArray: Object.values(messagesBySerial).sort(
      (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
    ),
    members,
    presenceMembers,
    roomStatus,
    handleSendMessage,
    handleDeleteMessage,
    handleToggleReaction,
  };
}

export interface ChatState {
  isLoaded: boolean;
  messageArray: Message[];
  members: Record<string, PresenceMember>; // or a typed member object if you have one
  roomStatus: string; // or enum/string union for statuses

  handleDeleteMessage: (message: Message) => Promise<void>;
  handleToggleReaction: (
    message: Message,
    reaction: ReactionTypeEntity,
  ) => Promise<void>;
  handleSendMessage: (message: string) => Promise<void>;
}

export const ChatContext = createContext<ChatState | undefined>(undefined);

export const ChatProvider = ({
  children,
  roomId,
  guests,
}: {
  children: React.ReactNode;
  roomId: string;
  guests?: string[];
}) => {
  const chat = useChat(roomId, guests);
  return <ChatContext.Provider value={chat}>{children}</ChatContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
